import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, IconButton, Tooltip, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { filter, some, unionBy } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import GridIcon from '../../../assets/icons/GridIcon.svg?react'
import GripLinesIcon from '../../../assets/icons/GripLines.svg?react'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { HeaderWrapper } from '../../../components/Wrapper/HeaderWrapper'
import YandexCloud from '../../../components/YandexCloud/YandexCloud'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import { useDiskStore } from '../../../store/storeDisk'
import { useRequestsStore } from '../../../store/storeRequests'
import { useWrapperStore } from '../../../store/storeWrapper'
import { SortingField } from '../../../swagger/dtoGateway'
import { theme } from '../../../theme'
import { DateFilter } from '../RecordsPage/components/DateFilter'
import { FileCardList } from '../RequestSettingPage/components/FileCardList'
import { FileTableList } from '../RequestSettingPage/components/FileTableList'
import CircleInformationIcon from './../../../assets/icons/CircleInformation.svg'
import { CreateButton } from './components/CreateButton'
import { DiskPageSkeleton } from './components/DiskPageSkeleton'
import { Filter } from './disk.constants'

export const DiskPage = ({ isChoiceFilesDialog = false }) => {
    const {
        fetchDisk,
        disk,
        isLoadingFetchDisk,
        isRefreshFetchDisk,
        setIsRefreshFetchDisk,
        error,
        setError,
    } = useDiskStore((state) => state)
    const { setStateNotification } = useWrapperStore((state) => state)
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { setFilesByStorage, filesByStorage } = useRequestsStore((state) => state)

    const [searchParams] = useSearchParams()
    const [folderName, setFolderName] = useState(undefined)
    const [viewFileList, setViewFileList] = useState('card')
    const [choiceFiles, setChoiceFiles] = useState([])
    const navigate = useNavigate()

    const handleChangeViewFileList = (event: React.SyntheticEvent, newValue: number) => {
        setViewFileList(newValue)
    }

    const handleClickRemoveChoiceFiles = () => {
        setChoiceFiles([])
    }

    const handleClickChoiceFiles = (file) => {
        setChoiceFiles((prev) => {
            const exists = some(prev, (f) => f.url === file.url)
            if (exists) {
                return filter(prev, (f) => f.url !== file.url)
            } else {
                return unionBy([...prev, file], 'url')
            }
        })
    }

    useEffect(() => {
        setFilesByStorage(choiceFiles)
    }, [choiceFiles])

    const searchParamFolder = searchParams.get('folder')
    const searchParamSorting = searchParams.get('desc')
    const searchParamOrdering = searchParams.get('order_by')

    useEffect(() => {
        setFolderName(searchParamFolder)
    }, [searchParamFolder])

    useEffect(() => {
        if (error) {
            setStateNotification({ isOpen: true, text: error, isError: true })
            setError(undefined)
        }
    }, [error])

    useEffect(() => {
        if (!isLoadingFetchDisk || isRefreshFetchDisk) {
            fetchDisk({
                prefix: searchParamFolder ? `/disk${searchParamFolder}` : '/disk/',
                desc: searchParamSorting,
                order_by: searchParamOrdering,
            })
            setIsRefreshFetchDisk(false)
        }
    }, [searchParamFolder, searchParamSorting, searchParamOrdering, isRefreshFetchDisk])

    return (
        <>
            <BlockWrapper
                sx={{
                    position: 'relative',
                    height: isChoiceFilesDialog
                        ? 'auto !important'
                        : `calc(100dvh - ${isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT}px)`,
                }}>
                <HeaderWrapper>
                    <Stack
                        direction="row"
                        sx={{
                            borderRadius: '29px',
                            '&:hover': {
                                borderRadius: '29px',
                            },
                            background: filesByStorage?.length > 0 ? '#F4F4F6' : 'none',
                            justifyContent:
                                filesByStorage?.length > 0 ? 'flex-start' : 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}>
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                            {!filesByStorage?.length && folderName && (
                                <IconButton
                                    sx={{
                                        '&:hover': { borderColor: '#F4F4F6' },
                                        background: ' #fff',
                                    }}
                                    variant="text"
                                    color="primary"
                                    onClick={() => navigate(-1)}>
                                    <ArrowBackIcon />
                                </IconButton>
                            )}

                            {!isChoiceFilesDialog && (
                                <Typography
                                    variant="Header3"
                                    sx={{
                                        color: '#1B1C1D'
                                    }}>
                                    Мой диск
                                    {folderName && (
                                        <span style={{ color: '#121317' }}>{folderName}</span>
                                    )}
                                </Typography>
                            )}
                            {!filesByStorage?.length && isChoiceFilesDialog && (
                                <Grid size={12}>
                                    <DateFilter
                                        filters={Filter}
                                        defaultFilter={SortingField.Filename}
                                    />
                                </Grid>
                            )}
                        </Stack>
                        {!filesByStorage?.length && (
                            <Stack
                                direction="row"
                                sx={{
                                    pl: 2,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                <Tabs
                                    value={viewFileList}
                                    onChange={handleChangeViewFileList}
                                    sx={{
                                        borderBottom: 'none',
                                        padding: 0,
                                        alignItems: 'center',
                                    }}>
                                    <Tab variant="secondary" value="card" icon={<GridIcon />} />
                                    <Tab
                                        variant="secondary"
                                        value="table"
                                        icon={<GripLinesIcon />}
                                        id="1"
                                        sx={{ p: 0 }}
                                    />
                                </Tabs>
                            </Stack>
                        )}
                        {filesByStorage?.length > 0 && (
                            <Stack
                                sx={{
                                    pl: 2,
                                    py: '4px',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                <Button
                                    startIcon={<CloseIcon />}
                                    variant="text"
                                    onClick={handleClickRemoveChoiceFiles}
                                    sx={{ background: '#F4F4F6', color: '#181B25', padding: '0' }}>
                                    Выбрано: {filesByStorage?.length}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </HeaderWrapper>

                <Grid
                    container
                    spacing={2}
                    m={2}
                    sx={{
                        [theme.breakpoints.down('sm')]: {
                            position: 'relative',
                        },
                    }}>
                    {!isChoiceFilesDialog && (
                        <Grid size={12}>
                            <DateFilter filters={Filter} defaultFilter={SortingField.Filename} />
                        </Grid>
                    )}

                    {(disk?.files?.length > 0 || disk?.folders?.length > 0) && (
                        <Grid
                            container
                            sx={{
                                width: '100%',
                                overflow: 'auto',
                                maxHeight: 'calc(100dvh - 310px)',
                            }}>
                            {viewFileList === 'card' && (
                                <FileCardList
                                    choiceFiles={choiceFiles}
                                    handleClickChoiceFiles={handleClickChoiceFiles}
                                    isChoiceFilesDialog={isChoiceFilesDialog}
                                    isReadOnly={false}
                                    files={disk.files}
                                    idEntity="disk"
                                    folders={disk.folders}
                                    isShowButton={false}
                                />
                            )}

                            {viewFileList === 'table' && (
                                <FileTableList
                                    choiceFiles={choiceFiles}
                                    handleClickChoiceFiles={handleClickChoiceFiles}
                                    isChoiceFilesDialog={isChoiceFilesDialog}
                                    isShowButton={false}
                                    folders={disk.folders}
                                    files={disk.files}
                                    idEntity="disk"
                                />
                            )}

                            {!isMobile && !isChoiceFilesDialog && (
                                <Grid
                                    container
                                    sx={{
                                        position: 'absolute',
                                        right: '30px',
                                        bottom: '16px',
                                    }}>
                                    <CreateButton
                                        folder={folderName ? `disk${folderName}` : 'disk'}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {isLoadingFetchDisk && <DiskPageSkeleton />}
                </Grid>

                {isMobile && !isChoiceFilesDialog && (
                    <Grid
                        container
                        sx={{
                            position: 'absolute',
                            width: 'calc(100% - 32px)',
                            right: 0,
                            bottom: 0,
                            margin: '0 16px',
                        }}>
                        <CreateButton
                            folder={folderName ? `disk${folderName}` : 'disk'}
                        />
                    </Grid>
                )}

                {!isLoadingFetchDisk &&
                    !(disk?.files?.length !== 0 || disk?.folders?.length !== 0) && (
                        <Grid container sx={{ justifyContent: 'center' }}>
                            <Grid
                                sx={{
                                    textAlign: 'center',
                                    mt: 10,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                                spacing={2}>
                                <Typography variant="h5" sx={{ color: '#181B25' }}>
                                    {folderName
                                        ? 'Эта папка пуста'
                                        : 'У вас нет загруженных файлов'}
                                </Typography>
                                <Typography
                                    variant="caption2"
                                    sx={{
                                        color: '#5A5F72',
                                        maxWidth: '252px',
                                        mt: 2,
                                        mb: 2,
                                    }}>
                                    Здесь будут отображаться все загруженные файлы и папки
                                </Typography>
                                {!isChoiceFilesDialog && (
                                    <CreateButton
                                        folder={folderName ? `disk${folderName}` : 'disk'}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    )}
                {!isChoiceFilesDialog && !isMobile ? (
                    <Tooltip title="Пользовательские данные надежно защищены технологиями Yandex Cloud в соответствии с российским законодательством и международными стандартами в области информационной безопасности и защиты данных">
                        <Box
                            sx={{
                                left: '16px',
                                position: 'absolute',
                                bottom: '16px',
                            }}>
                            <YandexCloud />
                        </Box>
                    </Tooltip>
                ) : null}
            </BlockWrapper>
            {!isChoiceFilesDialog && isMobile ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                    <YandexCloud />
                    <Tooltip
                        enterTouchDelay={0}
                        title="Пользовательские данные надежно защищены технологиями Yandex Cloud в соответствии с российским законодательством и международными стандартами в области информационной безопасности и защиты данных">
                        <IconButton>
                            <CircleInformationIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : null}
        </>
    )
}
